/**
 * WorkTrail — a trilha do TRABALHO e, agora, também as ações dele.
 *
 * ─── POR QUE ESTE CARD ABSORVEU O "PRÓXIMOS PASSOS" ─────────────────────────
 *
 * Havia dois cards vizinhos brigando: um chamado "Próximos passos" e uma linha
 * chamada "Próximo passo", dizendo coisas diferentes. O card antigo era
 * STATELESS (gated só por `canWrite` + id real), então oferecia "Gerar contrato
 * / OS / recibo / NFS-e" com o mesmo peso num orçamento pendente, num pago e num
 * que já tinha os três — e o "Gerar recibo" saía como Button3D verde, o elemento
 * mais proeminente da tela, num trabalho já quitado.
 *
 * Medido no aparelho antes da fusão: 270px (ações) + 338px (trilha) = ~632px de
 * um viewport de 780 — 81% da tela em dois blocos colados.
 *
 * ─── O DESENHO ──────────────────────────────────────────────────────────────
 *
 * Fechado: cabeçalho + trilha + UMA ação primária (a sugerida pela trilha).
 * Aberto: as demais ações + os documentos. O estado fechado já é informativo —
 * é o motivo de a trilha ser o resumo e não uma aba flutuante escrita "Próximos
 * passos", que não diria nada até ser tocada (e colidiria com a bottom nav, com
 * o "+" e com a faixa de toasts, que é calculada em `75dvh - nav/2`, ver #26).
 *
 * A lista de documentos entra no accordion junto com as ações porque é ela que
 * engorda o card (4 linhas dos 338px); recolher só as ações economizaria pouco.
 *
 * Nenhuma ação some quando a etapa já tem documento — muda o VERBO
 * (`workActionLabel`): "Gerar outro recibo". Esconder seria pior que o problema,
 * porque recibo de sinal e recibo de saldo são dois recibos legítimos.
 */
import { useState, type ReactNode } from 'react';
import { Check, ChevronDown, ChevronRight, Circle, Minus, Play, type LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { BRUTALIST_CARD_CLASS } from '@/lib/cardStyles';
import { cn } from '@/lib/utils';
import { workActionLabel, type Work, type WorkStage, type WorkStageId } from '@/lib/work';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

/** Uma ação que ESTA tela sabe executar. O rótulo vem da etapa, não da página. */
export interface WorkTrailAction {
  stage: WorkStageId;
  icon: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  /**
   * Mostrado quando desabilitado. Explicar por que não dá ("Aprove o orçamento
   * para gerar o recibo") é melhor que sumir com a ação — sumindo, o usuário
   * procura o que não existe mais.
   */
  hint?: string;
}

const isCancelledDetail = (stage: WorkStage) =>
  Boolean(stage.detail?.startsWith('Cancel')) || stage.detail === 'Recusado';

function StageMark({ stage }: { stage: WorkStage }) {
  if (stage.state === 'done') {
    return <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />;
  }
  if (stage.state === 'current') {
    return isCancelledDetail(stage) ? (
      <Circle className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
    ) : (
      <Play className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" />
    );
  }
  return <Minus className="h-3.5 w-3.5 text-muted-foreground/60" aria-hidden="true" />;
}

function stageBarClass(stage: WorkStage): string {
  if (stage.state === 'done') return 'bg-primary';
  if (stage.state === 'current') {
    return isCancelledDetail(stage) ? 'bg-muted-foreground/30' : 'bg-amber-500';
  }
  return 'bg-muted-foreground/20';
}

export interface WorkTrailProps {
  work: Work;
  /**
   * O documento de onde a tela está olhando. Ele não vira link para si mesmo —
   * um "abrir" que não sai do lugar parece um botão quebrado.
   */
  anchorId?: string | null;
  /** O que ESTA tela sabe fazer. A trilha decide qual vira a ação primária. */
  actions?: WorkTrailAction[];
  onOpenDocument?: (route: string) => void;
  /** Conteúdo extra dentro do accordion (avisos da própria tela). */
  children?: ReactNode;
  className?: string;
}

export function WorkTrail({
  work,
  anchorId,
  actions = [],
  onOpenDocument,
  children,
  className,
}: WorkTrailProps) {
  const [expanded, setExpanded] = useState(false);

  const stageById = (id: WorkStageId) => work.stages.find((s) => s.id === id)!;

  /**
   * A ação primária é a que a trilha sugere — e só quando a sugestão é COMEÇAR
   * algo. As ações desta lista criam documentos; "terminar" é mudança de estado
   * que a trilha não faz (aprovar orçamento, assinar contrato, concluir OS, dar
   * baixa no pagamento). Promover uma ação de criar a uma sugestão de terminar
   * troca uma coisa pela outra: com um lançamento em aberto, a sugestão é dar
   * baixa, e o botão oferecia "Gerar recibo" — recibo de dinheiro que ainda não
   * entrou. Nesses casos a sugestão vira texto e a ação fica no accordion.
   *
   * Num trabalho pago `nextStep` é `null`: não há botão nenhum e o card fica no
   * mínimo, que é o correto quando não há o que fazer.
   */
  const primary =
    work.nextStep?.kind === 'start'
      ? actions.find((a) => a.stage === work.nextStep!.stage && !a.disabled)
      : undefined;
  const secondary = actions.filter((a) => a !== primary);

  const documents = work.stages
    .flatMap((stage) => stage.documents.map((doc) => ({ ...doc, stage })))
    .filter((doc) => !anchorId || doc.id !== anchorId);

  const hasDrawer = secondary.length > 0 || documents.length > 0 || Boolean(children);

  return (
    <Card className={cn(BRUTALIST_CARD_CLASS, className)}>
      <CardContent className="space-y-3 p-4">
        <div>
          <div className="flex items-baseline justify-between gap-3">
            <p className="truncate text-sm font-semibold">{work.title}</p>
            {work.total > 0 && (
              <p className="shrink-0 text-sm font-semibold">{formatCurrency(work.total)}</p>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {work.clientName ? `${work.clientName} · ` : ''}
            {work.phaseLabel}
          </p>
        </div>

        {/* Trilha. Cinco colunas de largura igual: em 360px sobram ~54px por
            etapa, e por isso o rótulo é o curto (#51/#52). */}
        <div className="grid grid-cols-5 gap-1.5">
          {work.stages.map((stage) => (
            <div key={stage.id} className="min-w-0">
              <div className={cn('h-1 rounded-full', stageBarClass(stage))} />
              <div className="mt-1.5 flex justify-center">
                <StageMark stage={stage} />
              </div>
              <p
                className={cn(
                  'mt-0.5 text-center text-[10px] leading-tight',
                  stage.state === 'unused' ? 'text-muted-foreground/70' : 'text-muted-foreground',
                  stage.state === 'current' && 'font-medium text-foreground',
                )}
              >
                {stage.shortLabel}
              </p>
            </div>
          ))}
        </div>

        {work.paymentEvidenceLimited && (
          <p className="text-[11px] leading-snug text-muted-foreground">
            Você não tem acesso ao financeiro desta empresa, então o pagamento só aparece aqui se
            houver recibo.
          </p>
        )}

        {/* Ação primária: uma só, a sugerida. */}
        {primary && (
          <Button3D className="w-full" onClick={primary.onClick}>
            <primary.icon className="mr-2 h-4 w-4" />
            {workActionLabel(stageById(primary.stage))}
          </Button3D>
        )}
        {/* Sem ação executável aqui, a sugestão vira texto — nunca um botão que
            não faz nada. */}
        {!primary && work.nextStep && (
          <p className="text-center text-xs text-muted-foreground">
            Próximo passo: <span className="font-medium text-foreground">{work.nextStep.label}</span>
          </p>
        )}

        {hasDrawer && (
          <div className="border-t border-primary/20 pt-2">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="flex w-full items-center justify-between gap-2 rounded-md px-1 py-1.5 text-left text-xs text-muted-foreground hover:bg-primary/5"
            >
              <span>
                {secondary.length > 0 ? 'Outras ações' : 'Documentos'}
                {documents.length > 0 ? ` · ${documents.length} documento${documents.length > 1 ? 's' : ''}` : ''}
              </span>
              <ChevronDown
                className={cn('h-4 w-4 shrink-0 transition-transform', expanded && 'rotate-180')}
                aria-hidden="true"
              />
            </button>

            {expanded && (
              <div className="space-y-2 pt-2">
                {secondary.map((action) => {
                  const stage = stageById(action.stage);
                  return (
                    <div key={action.stage} className="space-y-1">
                      <Button
                        variant="outline"
                        className="w-full justify-between"
                        disabled={action.disabled}
                        onClick={action.onClick}
                      >
                        <span className="flex items-center">
                          <action.icon className="mr-2 h-4 w-4" />
                          {workActionLabel(stage)}
                        </span>
                        <ChevronRight className="h-4 w-4 opacity-60" aria-hidden="true" />
                      </Button>
                      {action.disabled && action.hint && (
                        <p className="pl-1 text-xs text-muted-foreground">{action.hint}</p>
                      )}
                    </div>
                  );
                })}

                {children}

                {documents.length > 0 && (
                  <div className="space-y-1 pt-1">
                    {documents.map((doc) => (
                      <button
                        key={`${doc.stage.id}-${doc.id}`}
                        type="button"
                        onClick={() => onOpenDocument?.(doc.route)}
                        className="flex w-full items-center justify-between gap-2 rounded-md px-1 py-1.5 text-left hover:bg-primary/5"
                      >
                        <span className="min-w-0">
                          <span
                            className={cn(
                              'block truncate text-xs font-medium',
                              doc.cancelled && 'text-muted-foreground line-through',
                            )}
                          >
                            {doc.label}
                          </span>
                          <span className="block text-[10px] text-muted-foreground">
                            {doc.stage.label}
                            {doc.hint ? ` · ${doc.hint}` : ''}
                          </span>
                        </span>
                        <ChevronRight className="h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
